import {PreloadAssetsAction} from "@dp-instant/2d";
import {Action, di, lazyGet} from "@dp-instant/core";
import {Types2D, TypesApp, TypesNotification, TypesUI} from "@dp-instant/interfaces";
import {PageModel} from "@dp-instant/social";
import {GenerateShareImageAction} from "@game/actions/GenerateShareImageAction";
import {SkinManager} from "@game/coregame/managers/SkinManager";
import {SelectableBuildingDataSet, SelectableFighterDataSet} from "@game/coregame/settings";
import {Playdeck} from "@game/coregame/utils/Playdeck";
import Root from "@game/coregame/view/Root";
import {TypesGame} from "@game/TypesGame";
import {DebugPopup} from "@game/ui/debug/DebugPopup";
import {TournamentsDebugPanel} from "@game/ui/debug/tounaments/TournamentsDebugPanel";
import {GiftPopupProps} from "@game/ui/popups/GiftPopup";
import {PopupType} from "@game/ui/PopupType";
import {inject, injectable} from "inversify";
import {BitmapFont, Texture, utils} from "pixi.js-legacy";

@injectable()
export class SIOPreloadAssetsAction extends PreloadAssetsAction {
    @inject(Types2D.rootView)
    private root: Root;

    @inject(TypesApp.pageModel)
    private readonly pageModel: PageModel;

    async execute(): Promise<any> {
        console.error("start preload");
        //cheats
        (() => {
            if (process.env.NODE_ENV === "development") {
                di.bind("debug-panel-popup").toConstantValue(DebugPopup);
                di.bind("debug-tournaments-popup").toConstantValue(TournamentsDebugPanel);

                const sendPush = () => {
                    lazyGet<Action>(TypesNotification.start)?.run();
                };
                const showWinPopup = () =>
                    lazyGet<Action>(TypesGame.actions.levelCompletePopup)?.run();

                const dumpTextureCache = () => {
                    const generatedPrefixes = ["sprite", "field"];
                    const allTextures = Object.values(utils.TextureCache);
                    const preloadedTexture = (t: any) => {
                        const ids = (t as Texture).textureCacheIds;
                        for (const id of ids) {
                            if (id.endsWith("png") || id.endsWith("svg")) {
                                return true;
                            }
                        }
                        return false;
                    };
                    const mapPartTexture = (t: any) => {
                        const ids = (t as Texture).textureCacheIds;
                        const [name] = ids;
                        return generatedPrefixes.includes(name?.split("-")[0]);
                    };
                    const textureToObj = (t: any) => ({
                        ids: t.textureCacheIds.join(", "),
                        frame: `${t.frame.width}x${t.frame.height}`
                    });
                    const generatedTextures = allTextures.filter(t => !preloadedTexture(t));
                    const mapParts = generatedTextures.filter(mapPartTexture);
                    const otherTextures = generatedTextures.filter(t => !mapPartTexture(t));
                    console.groupCollapsed("mapParts:", mapParts.length);
                    console.table(mapParts.map(t => (t as Texture).textureCacheIds[0]));
                    console.groupEnd();
                    console.group("otherTextures", otherTextures.length);
                    console.table(otherTextures.map(textureToObj));
                    console.groupEnd();
                    console.log(
                        "mapParts:",
                        mapParts.length,
                        "otherTextures",
                        otherTextures.length,
                        "allTextures",
                        allTextures.length
                    );
                };
                const showGiftPopup = () => {
                    const props: GiftPopupProps = {
                        captured: 5,
                        total: 5,
                        reward: lazyGet<SkinManager>(TypesGame.skinManager)?.getGift(),
                        onContinue: () => {
                            //
                        }
                    };
                    this.dispatch(TypesUI.events.POPUP, {id: PopupType.GIFT, props});
                };
                di.get<Action<any>>("flow.cheats")?.run([
                    () => {
                        setTimeout(() => {
                            let result = di
                                .get<GenerateShareImageAction>(GenerateShareImageAction)
                                .run({points: 100})
                                .then(() => dumpTextureCache());
                        }, 0);
                    },
                    showWinPopup,
                    dumpTextureCache
                ]);
            }
        })();

        const selectableItemTextures = [
            ...Array.from(
                SelectableBuildingDataSet.concat(SelectableFighterDataSet),
                item => item.textureUrl || ""
            )
        ];

        const progress = {assets: 0};

        BitmapFont.from(
            "Helvetica",
            {
                fill: "black",
                fontFamily: "Helvetica",
                fontSize: 24,
                fontWeight: "800"
            },
            {chars: "0123456789- ", resolution: devicePixelRatio}
        );

        const assets: string[] = [
            "Notification.png",
            "spinner.svg",
            "skull.svg",
            "finger.svg",
            "animation/win-rays.svg",
            "victory-framing.svg",
            ...selectableItemTextures
        ];

        window.playdeckUser = await Playdeck.getProfile();

        const assetsLoadingPromise = super.execute({
            assets,
            onProgress: (p: number) => {
                this.pageModel.assetsProgress = p / 100;
                Playdeck.loading(p);
            }
        });

        await Promise.all([assetsLoadingPromise]);

        utils.TextureCache["Notification.png"].baseTexture.setResolution(1);
        this.root.onAdded();
        this.root.spinner.show();
    }
}
